import { useEffect, useMemo, useState } from 'react';

import { EntityRefUtil, getIssueSeverityRank, IssueDTO, IssueSeverity, IssueStatus, WorkOrderStatus } from '@gearbelt/global';
import { useQueryClient } from '@tanstack/react-query';
import { AccessorKeyColumnDef, ColumnFiltersState, createColumnHelper, SortingState } from '@tanstack/react-table';
import { capitalize } from 'lodash';

import SearchBar from '@/components/common/search-bar';
import { TablePageHeaderActionButton } from '@/components/table-page/table-page-header';
import { Badge } from '@/components/ui/badge';
import { ButtonStateToggle } from '@/components/ui/button-state-toggle';
import { Checkbox } from '@/components/ui/checkbox';
import { DrawerTable } from '@/components/ui/drawer-table';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { columnFilterKeys } from '@/queries/column-filter';
import { getIssueSeverityColorClass } from '@/utils/issue';

import { CheckCircleIcon } from '../icons/check-circle';
import CloseIcon from '../icons/close';
import { OpenCircleIcon } from '../icons/open-circle';
import PlusIcon from '../icons/plus';
import UndoIcon from '../icons/undo';
import { WarningIcon } from '../icons/warning';

type IssueWithSelection = IssueDTO & { selected: boolean };

interface WorkOrderIssuesTableProps {
  issues: IssueWithSelection[];
  workOrderId?: string;
  workOrderNumber?: number;
  workOrderStatus?: WorkOrderStatus;
  isArchived: boolean;
  onToggleIssue: (issueId: string) => void;
  onToggleAll: (candidateIds: string[], allSelected: boolean) => void;
  onIssueClick: (issueId: string) => void;
  onCreateIssue: () => void;
  hasAsset: boolean;
  loading?: boolean;
}

export function WorkOrderIssuesTable({
  issues,
  workOrderId,
  workOrderNumber,
  workOrderStatus,
  isArchived,
  onToggleIssue,
  onToggleAll,
  onIssueClick,
  onCreateIssue,
  hasAsset,
  loading,
}: WorkOrderIssuesTableProps) {
  const isWorkOrderClosed = workOrderStatus === WorkOrderStatus.Closed;
  const queryClient = useQueryClient();

  const [statusFilter, setStatusFilter] = useState<'open' | 'closed'>('open');
  const [sorting, setSorting] = useState<{ key?: string; direction?: 'asc' | 'desc' }>({});
  const [search, setSearch] = useState('');
  const [openColumnFilters, setOpenColumnFilters] = useState<ColumnFiltersState>([]);
  const [closedColumnFilters, setClosedColumnFilters] = useState<ColumnFiltersState>(workOrderId ? [{ id: 'issueWorkOrder', value: [workOrderId] }] : []);

  const columnFilters = statusFilter === 'closed' ? closedColumnFilters : openColumnFilters;
  const setColumnFilters = statusFilter === 'closed' ? setClosedColumnFilters : setOpenColumnFilters;

  const woFilterIds = useMemo(() => {
    const activeFilters = statusFilter === 'closed' ? closedColumnFilters : openColumnFilters;
    return (activeFilters.find((f) => f.id === 'issueWorkOrder')?.value as string[] | undefined) ?? [];
  }, [statusFilter, closedColumnFilters, openColumnFilters]);
  const hasWoFilter = woFilterIds.length > 0;
  const hasNonDefaultFilter = hasWoFilter;

  const resetFilters = () => {
    if (statusFilter === 'closed') setClosedColumnFilters([]);
    else setOpenColumnFilters([]);
  };

  const woFilterOptions = useMemo(() => {
    const seen = new Map<string, string>();
    const statusFiltered = issues.filter((i) => (statusFilter === 'closed' ? i.status === IssueStatus.Closed : i.status !== IssueStatus.Closed));
    for (const issue of statusFiltered) {
      if (issue.workorderId && issue.workOrder?.number != null) {
        seen.set(issue.workorderId, EntityRefUtil.createWorkOrderRef(issue.workOrder.number));
      }
    }
    return Array.from(seen, ([value, label]) => ({ label, value }));
  }, [issues, statusFilter]);

  useEffect(() => {
    queryClient.invalidateQueries({ queryKey: columnFilterKeys.byColumn('issueWorkOrder') });
  }, [statusFilter, queryClient]);

  const filteredAndSorted = useMemo(() => {
    let filtered = [...issues];

    filtered = filtered.filter((i) => (statusFilter === 'closed' ? i.status === IssueStatus.Closed : i.status !== IssueStatus.Closed));

    if (hasWoFilter) {
      filtered = filtered.filter((i) => i.workorderId && woFilterIds.includes(i.workorderId));
    }

    if (search) {
      const q = search.toLowerCase();
      filtered = filtered.filter((i) => {
        const text = (i.title || i.description || i.fieldReference || '').toLowerCase();
        const woRef = i.workOrder?.number != null ? EntityRefUtil.createWorkOrderRef(i.workOrder.number).toLowerCase() : '';
        return text.includes(q) || woRef.includes(q);
      });
    }

    if (sorting.key) {
      const dir = sorting.direction === 'desc' ? -1 : 1;
      filtered.sort((a, b) => {
        switch (sorting.key) {
          case 'title': {
            const aVal = (a.title || a.description || a.fieldReference || '').toLowerCase();
            const bVal = (b.title || b.description || b.fieldReference || '').toLowerCase();
            return dir * aVal.localeCompare(bVal);
          }
          case 'severity':
            return dir * (getIssueSeverityRank(a.severity) - getIssueSeverityRank(b.severity));
          case 'status':
            return dir * (a.status ?? '').localeCompare(b.status ?? '');
          case 'issueWorkOrder': {
            const aNum = a.workOrder?.number ?? Infinity;
            const bNum = b.workOrder?.number ?? Infinity;
            return dir * (aNum - bNum);
          }
          default:
            return 0;
        }
      });
    } else {
      filtered.sort((a, b) => {
        const sevDiff = getIssueSeverityRank(a.severity) - getIssueSeverityRank(b.severity);
        if (sevDiff !== 0) return sevDiff;
        const aVal = (a.title || a.description || a.fieldReference || '').toLowerCase();
        const bVal = (b.title || b.description || b.fieldReference || '').toLowerCase();
        return aVal.localeCompare(bVal);
      });
    }

    return filtered;
  }, [issues, statusFilter, hasWoFilter, woFilterIds, search, sorting.key, sorting.direction]);

  const sortingState: SortingState = sorting.key ? [{ id: sorting.key, desc: sorting.direction === 'desc' }] : [];

  const visibleCandidates = useMemo(() => {
    return filteredAndSorted.filter((i) => {
      const isLinkedToOtherWo = !!i.workorderId && i.workorderId !== workOrderId;
      return !isLinkedToOtherWo;
    });
  }, [filteredAndSorted, workOrderId]);

  const allSelected = visibleCandidates.length > 0 && visibleCandidates.every((i) => i.selected);

  const handleToggleAll = () => {
    if (visibleCandidates.length === 0) return;
    onToggleAll(
      visibleCandidates.map((i) => i.id),
      allSelected,
    );
  };

  const columnHelper = createColumnHelper<IssueWithSelection>();

  const columns = [
    columnHelper.display({
      id: 'select',
      header: () => (
        <label className="-m-2 flex cursor-pointer p-2" onClick={(e) => e.stopPropagation()}>
          <Checkbox
            checked={allSelected}
            disabled={isWorkOrderClosed || visibleCandidates.length === 0}
            onCheckedChange={handleToggleAll}
            aria-label="Select all issues"
          />
        </label>
      ),
      cell: ({ row }) => {
        const issue = row.original;
        const isLinkedToOtherWo = !!issue.workorderId && issue.workorderId !== workOrderId;
        const checked = issue.selected || isLinkedToOtherWo;
        return (
          <label className="-m-2 flex cursor-pointer p-2" onClick={(e) => e.stopPropagation()}>
            <Checkbox checked={checked} disabled={isWorkOrderClosed || isLinkedToOtherWo || isArchived} onCheckedChange={() => onToggleIssue(issue.id)} />
          </label>
        );
      },
      meta: { cellClassName: 'w-[40px]' },
    }),
    columnHelper.accessor((i) => i.title || i.description || i.fieldReference || '', {
      id: 'title',
      header: 'Title',
      cell: ({ row }) => {
        const title = row.original.title || row.original.description || row.original.fieldReference || '';
        return title ? <span className="block truncate">{title}</span> : <span className="text-secondary-text">-</span>;
      },
      meta: { cellClassName: 'max-w-0' },
    }),
    columnHelper.accessor('severity', {
      id: 'severity',
      header: 'Severity',
      cell: ({ row }) => {
        const value = row.original.severity;
        if (!value) return <span className="text-secondary-text">-</span>;
        const colorClass = getIssueSeverityColorClass(value);
        const icon =
          value === IssueSeverity.Critical ? (
            <WarningIcon size={20} className="text-default-red" />
          ) : (
            <div className={cn('mx-[3px] h-[15px] w-[15px] rounded-full border-[3px]', colorClass)} />
          );
        return (
          <div className="flex items-center gap-[5px]">
            {icon}
            <span className="text-sm">{capitalize(value)}</span>
          </div>
        );
      },
      meta: { cellClassName: 'w-[110px]' },
    }),
    columnHelper.accessor((i) => (i.status === IssueStatus.Closed ? 'Closed' : 'Open'), {
      id: 'status',
      header: 'Status',
      cell: ({ row }) =>
        row.original.status === IssueStatus.Closed ? (
          <Badge variant="success" size="sm">
            Closed
          </Badge>
        ) : (
          <Badge variant="grey" size="sm">
            Open
          </Badge>
        ),
      meta: { cellClassName: 'w-[90px]' },
    }),
    columnHelper.accessor((i) => i.workorderId ?? '', {
      id: 'issueWorkOrder',
      header: 'Work Order',
      filterFn: 'arrIncludesSome',
      cell: ({ row }) => {
        const issue = row.original;
        const isLinkedToOtherWo = !!issue.workorderId && issue.workorderId !== workOrderId;
        if (isLinkedToOtherWo) {
          const linkedNumber = issue.workOrder?.number;
          if (!linkedNumber) return <span className="text-secondary-text">-</span>;
          const ref = EntityRefUtil.createWorkOrderRef(linkedNumber);
          return (
            <div className="flex items-center gap-1">
              <span className="truncate">{ref}</span>
              <TooltipProvider>
                <Tooltip delayDuration={0}>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      aria-label={`Remove from ${ref}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        onToggleIssue(issue.id);
                      }}
                      className="text-grey-5 hover:text-default-text -m-1 rounded p-1 transition-colors">
                      <CloseIcon className="h-3.5 w-3.5" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent className="bg-default-text pointer-events-none border-none py-1 text-xs text-white">Remove from {ref}</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          );
        }
        const isAlreadyLinkedToThisWo = !!issue.workorderId && issue.workorderId === workOrderId;
        const isPendingAddition = issue.selected && !isAlreadyLinkedToThisWo;
        const isPendingRemoval = !issue.selected && isAlreadyLinkedToThisWo;

        if (isAlreadyLinkedToThisWo && !isPendingRemoval) {
          const ref = workOrderNumber ? EntityRefUtil.createWorkOrderRef(workOrderNumber) : '-';
          return <span className="truncate">{ref}</span>;
        }
        if (isPendingAddition) {
          const ref = workOrderNumber ? EntityRefUtil.createWorkOrderRef(workOrderNumber) : 'New';
          return (
            <div className="flex items-center gap-1">
              <span className="truncate">{ref}</span>
              <div className="bg-default-green size-1.5 shrink-0 rounded-full" />
            </div>
          );
        }
        if (isPendingRemoval) {
          const ref = workOrderNumber ? EntityRefUtil.createWorkOrderRef(workOrderNumber) : '-';
          return (
            <div className="flex items-center gap-1">
              <span className="relative truncate">
                <span className="invisible">{ref}</span>
                <span className="text-secondary-text absolute inset-0">-</span>
              </span>
              <div className="bg-default-orange size-1.5 shrink-0 rounded-full" />
            </div>
          );
        }
        return <span className="text-secondary-text">-</span>;
      },
      meta: {
        cellClassName: 'w-[145px]',
        filterType: 'multi-select' as const,
        disableNullFilter: true,
        getFilterOptionsFn: async () => woFilterOptions,
      },
    }),
  ] as AccessorKeyColumnDef<IssueWithSelection>[];

  return (
    <div className="mb-1 flex flex-col gap-1">
      <Label>Issues</Label>
      <div className="border-grey-3 overflow-clip rounded border">
        <div className={cn('bg-grey-2 border-grey-3 flex min-h-[45px] items-center gap-2 border-b px-1', loading && 'pointer-events-none opacity-50')}>
          <ButtonStateToggle
            options={[
              { icon: <OpenCircleIcon className="h-4 w-4" />, label: 'Open', value: 'open' },
              { icon: <CheckCircleIcon className="h-4 w-4" />, label: 'Closed', value: 'closed' },
            ]}
            value={statusFilter}
            onChange={setStatusFilter}
          />
          <SearchBar
            search={search}
            setSearch={setSearch}
            containerClassName="max-w-[300px] flex-1"
            placeholder="Search issues..."
            aria-label="Search issues"
          />
          <TablePageHeaderActionButton
            type="button"
            icon={<UndoIcon className="h-4 w-4" />}
            label="Reset filters"
            kind="secondary"
            onClick={resetFilters}
            className={hasNonDefaultFilter ? '' : 'invisible'}
          />
          {!isWorkOrderClosed && !isArchived && hasAsset && (
            <div className="ml-auto">
              <TablePageHeaderActionButton
                type="button"
                icon={<PlusIcon className="h-4 w-4" />}
                label="Create Issue"
                variant="success"
                onClick={onCreateIssue}
              />
            </div>
          )}
        </div>
        {loading ? (
          <table className="w-full table-fixed border-collapse text-sm">
            <thead>
              <tr>
                <th className="bg-grey-1 w-[40px] p-2 text-left font-medium">
                  <Skeleton className="size-4 rounded" />
                </th>
                <th className="bg-grey-1 p-2 text-left font-medium">Title</th>
                <th className="bg-grey-1 w-[110px] p-2 text-left font-medium">Severity</th>
                <th className="bg-grey-1 w-[90px] p-2 text-left font-medium">Status</th>
                <th className="bg-grey-1 w-[145px] p-2 text-left font-medium">Work Order</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-grey-3 border-t">
                <td className="px-2 py-1">
                  <Skeleton className="size-4 rounded" />
                </td>
                <td className="px-2 py-1">
                  <Skeleton className="h-4 w-3/4" />
                </td>
                <td className="px-2 py-1">
                  <Skeleton className="h-4 w-16" />
                </td>
                <td className="px-2 py-1">
                  <Skeleton className="h-5 w-14 rounded-full" />
                </td>
                <td className="px-2 py-1">
                  <Skeleton className="h-4 w-20" />
                </td>
              </tr>
            </tbody>
          </table>
        ) : (
          <DrawerTable
            data={filteredAndSorted}
            columns={columns}
            columnFilters={columnFilters}
            onColumnFiltersChange={setColumnFilters}
            onRowClick={(row) => onIssueClick(row.id)}
            onSortingChange={setSorting}
            sorting={sortingState}
            notFoundDisplay={
              <div className="text-secondary-text py-1 text-center text-sm">
                {search || hasNonDefaultFilter ? 'No matching issues' : hasAsset ? 'No issues for this asset' : 'No asset selected'}
              </div>
            }
          />
        )}
      </div>
    </div>
  );
}
